/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { getImageAtSize } from '../../../lib/utils';

import { useLabels } from '../../../contexts/LabelsContext';
import StyledButton from '../../buttons/StyledButton';
import ArrowIcon from '../../icons/ArrowIcon';
import CheckIcon from '../../icons/Check';
import LockIcon from '../../icons/Lock';

import styles from '../../../../styles/themes/iga/iga-tablee-game-item.module.css';

interface IgaTableeGameItemProps {
    index: number;
    game: Game;
    participation?: Participation | null;
    games: Game[];
    participations: Participation[];
    className?: string | null;
    cardClassName?: string | null;
}

function IgaTableeGameItem({
    index,
    game,
    participation,
    games,
    participations,
    className = null,
    cardClassName = null,
}: IgaTableeGameItemProps) {
    const url = useUrlGenerator();
    const { unusedPincode = null } = useUser() || {};
    const { unlockTitle, unlockDescription } = useLabels();
    const { id: gameId, title, subtitle, image, order } = game;
    const {
        id: participationId,
        ended = false,
        pincode: participationPincode = null,
    } = participation || {};
    const { url: imageUrl = null } = getImageAtSize(image, 'medium') || {};
    const lastUnparticipedGameIndex = games.findIndex(
        ({ id }) => participations.findIndex(({ gameId }) => gameId === id) === -1,
    );

    const nextUnlockedIndex = lastUnparticipedGameIndex;
    // const pincode = participationPincode || unusedPincode;
    const unlocked =
        participationPincode !== null || (unusedPincode !== null && index === nextUnlockedIndex);
    const canBeUnlocked = unusedPincode === null && index === nextUnlockedIndex;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.completed]: ended,
                    [styles.locked]: !unlocked,
                    [styles.canBeUnlocked]: canBeUnlocked,
                },
                className,
            ])}
        >
            <div className={styles.inner}>
                {canBeUnlocked && index > 0 ? (
                    <div className={styles.message}>
                        <h4>{unlockTitle}</h4>
                        <p>{unlockDescription}</p>
                    </div>
                ) : null}

                <div className={classNames([styles.card, cardClassName])}>
                    <div className={styles.label}>
                        {subtitle !== null ? (
                            <div className={styles.participations}>{subtitle}</div>
                        ) : null}
                        <div className={styles.subtitle}>Quiz {order}</div>
                        <h4 className={styles.title}>{title}</h4>

                        <StyledButton
                            href={
                                unlocked || canBeUnlocked
                                    ? url(participation !== null ? 'participation' : 'game', {
                                          game: gameId,
                                          participation: participationId,
                                      })
                                    : null
                            }
                            icon={
                                ended ? (
                                    <CheckIcon className={styles.icon} />
                                ) : unlocked ? (
                                    <ArrowIcon className={styles.icon} />
                                ) : (
                                    <LockIcon className={styles.icon} />
                                )
                            }
                            disabled={!unlocked && !canBeUnlocked}
                            iconPosition="right"
                            className={styles.button}
                        >
                            {ended ? (
                                <FormattedMessage
                                    defaultMessage="Completed"
                                    description="Button label"
                                />
                            ) : unlocked ? (
                                <FormattedMessage
                                    defaultMessage="Answer"
                                    description="Button label"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Unlock"
                                    description="Button label"
                                />
                            )}
                        </StyledButton>
                    </div>

                    <img src={imageUrl} className={styles.image} />
                </div>
            </div>
        </div>
    );
}

export default IgaTableeGameItem;
